import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useDispatch, useSelector } from "react-redux";
import { GetClaimsApi } from "./ClaimsAPI";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  Search,
  Filter,
  Eye,
  Laptop,
  Smartphone,
  Headphones,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function Claims({ onViewClaim = () => {} }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const totalCount = useSelector((state) => state.claims.totalCount || 0);
  const [daysFilter, setDaysFilter] = useState("30-days");
  const claimRecords = useSelector((state) => state.claims.claimsRecords);

  // 📱 Icon by plan
  const getProductIcon = (planId) => {
    if (planId.includes("laptop") || planId.includes("macbook")) {
      return <Laptop size={16} className="text-xaxathrive-primary" />;
    }
    if (planId.includes("phone") || planId.includes("iphone")) {
      return <Smartphone size={16} className="text-xaxathrive-primary" />;
    }
    return <Headphones size={16} className="text-xaxathrive-primary" />;
  };
  const getCreatedAfterDate = () => {
    const today = new Date();
    switch (daysFilter) {
      case "7-days":
        return new Date(today.setDate(today.getDate() - 7)).toISOString();
      case "30-days":
        return new Date(today.setDate(today.getDate() - 30)).toISOString();
      case "90-days":
        return new Date(today.setDate(today.getDate() - 90)).toISOString();
      default:
        return null;
    }
  };
  useEffect(() => {
    const statusToSend = statusFilter === "all" ? null : statusFilter;
    const createdAfter = daysFilter === "custom" ? null : getCreatedAfterDate();

    dispatch(
      GetClaimsApi({
        search: searchTerm || "",
        status: statusToSend,
        createdAfter: createdAfter,
        pageNumber: page,
        pageSize: pageSize,
      })
    );
  }, [dispatch, searchTerm, page, pageSize, statusFilter, daysFilter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Claims Management
        </h1>
        <p className="text-slate-600">
          Review and manage protection plan claims
        </p>
      </div>

      {/* Filter/Search Bar */}
      <Card>
        <CardContent className="!p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={16}
              />
              <Input
                placeholder="Search claims, customers, or plan IDs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="!w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending review">Pending Review</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="solved">Solved</SelectItem>
                </SelectContent>
              </Select>

              <Select value={daysFilter} onValueChange={setDaysFilter}>
                <SelectTrigger className="!w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7-days">Last 7 days</SelectItem>
                  <SelectItem value="30-days">Last 30 days</SelectItem>
                  <SelectItem value="90-days">Last 90 days</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setDaysFilter("30-days");
                  setPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Claims Table */}
      <Card>
        <CardContent className="!p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="w-10">
                    <Checkbox />
                  </TableHead>
                  <TableHead>Claim ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>AI Score</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claimRecords?.map((claim) => (
                  <TableRow
                    key={claim.claimID}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/claimview/${claim.productName
                          .trim()
                          .replace(/\s+/g, "-")}`
                      )
                    }
                  >
                    <TableCell>
                      <Checkbox />
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {claim.businessClaimID}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">
                          {claim.customerName}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{claim.productName}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm capitalize">
                        {claim.issueType?.replace("_", " ") || "Unknown"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">
                        ${claim.claimAmount || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge>{claim.status}</Badge>
                    </TableCell>
                    <TableCell>{claim.aiScore}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(claim.createdDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/claimview/${claim.productName
                              .trim()
                              .replace(/\s+/g, "-")}`
                          )
                        }
                      >
                        <Eye size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Showing{" "}
                <span className="font-medium">{(page - 1) * pageSize + 1}</span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(page * pageSize, totalCount)}
                </span>{" "}
                of <span className="font-medium">{totalCount}</span> results
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((prev) => prev - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button size="sm" className="bg-xaxathrive-primary text-white">
                  {page}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page * pageSize >= totalCount}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Claims;
